import { createApi } from "@reduxjs/toolkit/query/react";
import { baseRestQueryResult as baseQuery, showNotification } from "@/services/api";
import { ListResponse } from "@/types/response.type";
import { BusinessUnit, BusinessUnitForm, DropDownBusinessUnits } from "@/types/businessunit.type";

interface GetDropDownArgs {
  status?: string,
  room?: boolean
  checkExpired?: boolean
  actionAdd?: number
}

// Define a service using a base URL and expected endpoints
export const businessApi = createApi({
  reducerPath: "businessApi",
  baseQuery,
  tagTypes:['business-units'],
  endpoints: (builder) => ({
    getBusinessUnits: builder.query<ListResponse<BusinessUnit>, {}>({
      query: (data) => ({ url: `business-unit`, params: data }),
      keepUnusedDataFor: 0,
      transformResponse: (rawResult: { data: ListResponse<BusinessUnit> }, meta) => {
        return rawResult.data
      },
    }),
    getDropDownBusinessUnits: builder.query<DropDownBusinessUnits[], GetDropDownArgs>({
      query: (data) => ({ url:  `business-unit/dropdown`, params: data }),
      keepUnusedDataFor: 0,
      transformResponse: (rawResult: { data: DropDownBusinessUnits[] }, meta) => {
        return rawResult?.data || []
      },
    }),
    getBusinessUnit: builder.query<BusinessUnit, string>({
      query: (id) => `business-unit/${id}`,
      keepUnusedDataFor: 0,
      transformResponse: (rawResult: { data: BusinessUnit }, meta) => {
        return rawResult.data
      },
    }),
    addBusinessUnit: builder.mutation<any, BusinessUnitForm>({
      query: (body) => ({
        url: 'business-unit',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: { data: string }, meta) => {
        return rawResult.data
      },
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("POST", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          // err?.error?.data?.message
          showNotification("POST", "error", "Add property have error")
        }
      }
    }),
    editBusinessUnit: builder.mutation<BusinessUnit, Pick<BusinessUnit, "id">>({
      query: ({ id, ...body }) => ({
        url: `business-unit/${id}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("PUT", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          showNotification("PUT", "error", "Edit property have error")
        }
      }
    }),
    deleteBusinessUnit: builder.mutation<any, string>({
      query: (id) => ({
        url: `business-unit/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("DELETE", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          showNotification("DELETE", "error", "Delete property have error")
        }
      }
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetBusinessUnitsQuery, useGetDropDownBusinessUnitsQuery, useLazyGetDropDownBusinessUnitsQuery, useGetBusinessUnitQuery, useLazyGetBusinessUnitQuery, useAddBusinessUnitMutation, useEditBusinessUnitMutation, useDeleteBusinessUnitMutation } = businessApi;
